import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getRouteApi } from "@tanstack/react-router";
import { DateTime } from "luxon";

import {
  extractPlayerActivityForTimePeriod,
  extractStatsPerGame,
} from "./helpers";

import { Container, Grid, Text, Center } from "@mantine/core";
//
import { DateRangeSelect } from "./date-range-select";
// charts
import { CooldownsChart } from "./charts/cooldowns-chart";
import { PlayerActivityChart } from "./charts/player-activity-chart";
import { GamesPlayedChart } from "./charts/games-played";

const route = getRouteApi("/_authenticated/app/players/$playerId/insights");

const SectionTitle: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Text fw={700} td="underline">
      {children}
    </Text>
  );
};

export const PlayerInsightsPage: React.FC = () => {
  const { t } = useTranslation();
  const { playerActivityResp } = route.useLoaderData();
  const {
    granularity,
    startDate: startDateStr,
    endDate: endDateStr,
  } = route.useSearch();

  const startDate = DateTime.fromISO(startDateStr);
  const endDate = DateTime.fromISO(endDateStr);

  const playerActivityInTimeRange = useMemo(() => {
    return extractPlayerActivityForTimePeriod(
      playerActivityResp.results,
      startDate,
      endDate,
    );
  }, [playerActivityResp.results, startDate, endDate]);

  const gamePlaytimeStats = useMemo(() => {
    return extractStatsPerGame(playerActivityInTimeRange);
  }, [playerActivityInTimeRange]);

  return (
    <Container>
      <Grid gutter="xl">
        <Grid.Col span={12}>
          <SectionTitle>
            {t("playerInsights.configureSection.title")}
          </SectionTitle>
          <DateRangeSelect />
        </Grid.Col>

        <Grid.Col span={{ xs: 12, md: 6 }}>
          <SectionTitle>
            {t("playerInsights.playtimeSection.title")}
          </SectionTitle>
          <PlayerActivityChart
            playerActivityInTimeRange={playerActivityInTimeRange}
            dateRangeStart={startDate}
            dateRangeEnd={endDate}
            granularity={granularity}
          />
        </Grid.Col>

        <Grid.Col span={{ xs: 12, md: 6 }}>
          <SectionTitle>
            {t("playerInsights.cooldownsSection.title")}
          </SectionTitle>
          <CooldownsChart
            playerActivityInTimeRange={playerActivityInTimeRange}
            dateRangeStart={startDate}
            dateRangeEnd={endDate}
            granularity={granularity}
          />
        </Grid.Col>

        {/* note the empty columns that take up surrounding space */}
        <Grid.Col span={{ xs: 12, md: 8 }} offset={{ xs: 0, md: 2 }}>
          <SectionTitle>
            {t("playerInsights.gamesPlayedSection.title")}
          </SectionTitle>
          <Center mt="xl">
            <GamesPlayedChart gameStats={gamePlaytimeStats} />
          </Center>
        </Grid.Col>
      </Grid>
    </Container>
  );
};
